/**
 * Greeks Cache Service - Client Side Only
 * Manages caching and validation of Greeks data in the browser
 */

import type { GreeksData } from "@/services/market-data-streamer";
import { symbolMappingService } from "./symbol-mapping-service";
import {
  deriveSymbolVariants,
  buildAliasKeys,
  removeLegacyAliases,
} from "./symbol-alias-utils";

type UnknownRecord = Record<string, unknown>;

const isRecord = (value: unknown): value is UnknownRecord =>
  typeof value === "object" && value !== null;

const readString = (
  record: UnknownRecord,
  keys: string[],
): string | undefined => {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === "string" && value.trim() !== "") {
      return value;
    }
  }
  return undefined;
};

const readNumber = (
  record: UnknownRecord,
  keys: string[],
): number | undefined => {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === "number" && Number.isFinite(value)) {
      return value;
    }
    if (typeof value === "string" && value.trim() !== "") {
      const parsed = Number(value);
      if (Number.isFinite(parsed)) {
        return parsed;
      }
    }
  }
  return undefined;
};

export interface CachedGreeks {
  symbol: string;
  streamerSymbol: string;
  delta: number;
  gamma: number;
  theta: number;
  vega: number;
  rho: number;
  timestamp: number;
  updateCount: number;
  lastValidUpdate: number;
  confidence: number; // 0-1 score based on data consistency
  isStale?: boolean; // Flag indicating if data is older than preferred threshold
  impliedVolatility?: number;
}

interface GreeksValidation {
  isValid: boolean;
  reason?: string;
  confidence: number;
}

export class GreeksCacheService {
  // In-memory cache
  private cache = new Map<string, CachedGreeks>();

  // History for validation (keep last N values)
  private history = new Map<string, GreeksData[]>();
  private maxHistorySize = 10;

  // Validation thresholds
  private readonly CONFIDENCE_THRESHOLD = 0.7;
  private readonly MIN_UPDATES_FOR_HIGH_CONFIDENCE = 3;

  private resolveStreamerSymbol(record: UnknownRecord): string | null {
    const storedStreamer = readString(record, [
      "streamerSymbol",
      "streamer_symbol",
    ]);
    if (storedStreamer && storedStreamer.startsWith(".")) {
      return storedStreamer;
    }

    const symbol = readString(record, [
      "symbol",
      "positionSymbol",
      "position_symbol",
    ]);
    if (symbol && symbol.startsWith(".")) {
      return symbol;
    }

    const candidates = [storedStreamer, symbol];
    for (const candidate of candidates) {
      if (candidate && candidate.includes(" ")) {
        const converted = symbolMappingService.getStreamerSymbol(candidate);
        if (converted) {
          return converted;
        }
      }
    }

    return null;
  }

  private buildCachedGreeks(
    record: UnknownRecord,
    streamerSymbol: string,
  ): CachedGreeks {
    const now = Date.now();
    const symbol = readString(record, ["symbol", "positionSymbol", "position_symbol"]) ??
      streamerSymbol;

    return {
      symbol,
      streamerSymbol,
      delta: readNumber(record, ["delta"]) ?? 0,
      gamma: readNumber(record, ["gamma"]) ?? 0,
      theta: readNumber(record, ["theta"]) ?? 0,
      vega: readNumber(record, ["vega"]) ?? 0,
      rho: readNumber(record, ["rho"]) ?? 0,
      timestamp: readNumber(record, ["timestamp"]) ?? now,
      updateCount: readNumber(record, ["updateCount", "update_count"]) ?? 0,
      lastValidUpdate:
        readNumber(record, ["lastValidUpdate", "last_valid_update", "timestamp"]) ??
        now,
      confidence: readNumber(record, ["confidence"]) ?? 1,
      impliedVolatility: readNumber(record, [
        "impliedVolatility",
        "implied_volatility",
        "iv",
      ]),
    };
  }

  private parseCachedGreeksEntry(entry: unknown): CachedGreeks | null {
    if (!isRecord(entry)) {
      return null;
    }

    const streamerSymbol = this.resolveStreamerSymbol(entry);
    if (!streamerSymbol || !streamerSymbol.startsWith(".")) {
      return null;
    }

    return this.buildCachedGreeks(entry, streamerSymbol);
  }

  constructor() {
    // Initialize from localStorage if available
    this.loadFromStorage();
    this.rebuildCacheIndex();

    // REMOVED: Automatic cleanup - we prioritize data persistence
    // Data should persist across sessions and market closes

    // Save to storage periodically to ensure persistence
    setInterval(() => {
      if (this.cache.size > 0) {
        this.saveToStorage();
      }
    }, 30000); // Every 30 seconds

    // Also save on page unload
    if (typeof window !== "undefined") {
      window.addEventListener("beforeunload", () => {
        this.saveToStorage();
      });

      // Load from database on initialization. Skipped on /glance: the
      // secret-link read-only page has no broker session, so this call can
      // only 401 (console noise on a page that must load clean) and glance
      // never consumes client-side greeks.
      if (!window.location.pathname.startsWith("/glance")) {
        this.loadFromDatabase();
      }
    }
  }

  /**
   * Process incoming Greeks data
   */
  processGreeksUpdate(greeksData: GreeksData): void {
    // Removed continuous logging that was causing high CPU usage

    const validation = this.validateGreeks(greeksData);

    if (greeksData.eventSymbol?.includes("SOFI251010C30.5")) {
      console.log('[ClientGreeksCache][sofi] incoming', {
        eventSymbol: greeksData.eventSymbol,
        delta: greeksData.delta,
        gamma: greeksData.gamma,
        theta: greeksData.theta,
        vega: greeksData.vega,
        validation,
      });
    }

    if (!validation.isValid) {
      // Only log validation failures in debug mode
      if (process.env.NEXT_PUBLIC_DEBUG_GREEKS === "true") {
        // Greeks validation failed
      }
      this.addToHistory(greeksData.eventSymbol, greeksData);
      return;
    }

    // Update cache with valid data
    this.updateCache(greeksData, validation.confidence);
  }

  /**
   * Validate Greeks data - More permissive validation
   */
  private validateGreeks(greeksData: GreeksData): GreeksValidation {
    // Check for missing data
    if (greeksData.delta === undefined || greeksData.delta === null || Number.isNaN(greeksData.delta)) {
      return { isValid: false, reason: "Missing delta", confidence: 0 };
    }

    const delta = greeksData.delta;

    // Only reject if delta is outside theoretical bounds
    if (delta < -1 || delta > 1) {
      return { isValid: false, reason: "Delta out of bounds", confidence: 0 };
    }

    // Delta of 0 or 1 can be valid for ATM or deep ITM/OTM options
    // Don't reject these values

    // Compare with historical data but with larger tolerance
    const history = this.history.get(greeksData.eventSymbol) || [];
    if (history.length >= 2) {
      const recentDeltas = history
        .slice(-3)
        .map((h) => h.delta)
        .filter((d) => d !== undefined);
      const avgDelta =
        recentDeltas.reduce((a, b) => a + b, 0) / recentDeltas.length;

      // Increase tolerance for delta changes (was 0.5, now 0.8)
      if (Math.abs(delta - avgDelta) > 0.8) {
        return { isValid: false, reason: "Large delta jump", confidence: 0.4 };
      }
    }

    // Calculate confidence based on data consistency
    let confidence = 0.9;

    // Don't reduce confidence for edge values as they can be valid

    // Increase confidence if other Greeks look reasonable
    if (greeksData.gamma !== undefined && Math.abs(greeksData.gamma) < 1) {
      confidence = Math.min(confidence * 1.1, 1);
    }

    return { isValid: true, confidence };
  }

  /**
   * Update cache with validated data
   */
  private updateCache(greeksData: GreeksData, confidence: number): void {
    const rawEventSymbol =
      typeof greeksData.eventSymbol === "string"
        ? greeksData.eventSymbol.trim()
        : "";
    if (!rawEventSymbol) {
      return;
    }

    const variants = deriveSymbolVariants(
      rawEventSymbol,
      symbolMappingService,
    );

    if (variants.canonicalStreamer && variants.canonicalPosition) {
      symbolMappingService.addWebSocketMapping(
        variants.canonicalStreamer,
        variants.canonicalPosition,
      );
    }

    const aliasKeys = buildAliasKeys(
      [
        rawEventSymbol,
        variants.trimmed,
        variants.mappingPosition,
        variants.canonicalStreamer,
        variants.canonicalPosition,
        variants.compactSymbol,
      ],
      { hasKey: this.cache.has.bind(this.cache) },
    );

    if (aliasKeys.length === 0) {
      aliasKeys.push(rawEventSymbol);
    }

    let existing: CachedGreeks | null = null;
    for (const key of aliasKeys) {
      const candidate = this.cache.get(key);
      if (candidate) {
        existing = candidate;
        break;
      }
    }

    if (existing && greeksData.timestamp <= existing.timestamp) {
      return;
    }

    const primaryStreamerSymbol =
      variants.canonicalStreamer ??
      existing?.streamerSymbol ??
      variants.trimmed;

    const resolvedPositionSymbol =
      variants.canonicalPosition ??
      variants.mappingPosition ??
      existing?.symbol ??
      primaryStreamerSymbol;

    const cached: CachedGreeks = {
      symbol: resolvedPositionSymbol,
      streamerSymbol: primaryStreamerSymbol,
      delta: greeksData.delta ?? 0,
      gamma: greeksData.gamma ?? 0,
      theta: greeksData.theta ?? 0,
      vega: greeksData.vega ?? 0,
      rho: greeksData.rho ?? 0,
      timestamp: greeksData.timestamp,
      updateCount: (existing?.updateCount || 0) + 1,
      lastValidUpdate: greeksData.timestamp,
      confidence,
    };

    if (
      existing &&
      existing.updateCount >= this.MIN_UPDATES_FOR_HIGH_CONFIDENCE
    ) {
      cached.confidence = Math.min(confidence * 1.2, 1);
    }

    const preservedKeys = new Set(aliasKeys);
    aliasKeys.forEach((key) => {
      if (key) {
        this.cache.set(key, cached);
      }
    });

    if (existing && existing !== cached) {
      removeLegacyAliases(this.cache, existing, preservedKeys);
    }

    this.addToHistory(primaryStreamerSymbol, greeksData);

    this.saveToStorage();
    void this.saveToDatabase(cached);

    if (process.env.NEXT_PUBLIC_DEBUG_GREEKS === "true") {
      // Greeks cached
    }

    if (resolvedPositionSymbol.includes("SOFI251010C30.5")) {
      console.log('[ClientGreeksCache][sofi] cached', {
        positionSymbol: resolvedPositionSymbol,
        streamerSymbol: primaryStreamerSymbol,
        aliases: Array.from(preservedKeys),
        cached,
      });
    }
  }

  /**
   * Add Greeks data to history
   */
  private addToHistory(symbol: string, greeksData: GreeksData): void {
    const history = this.history.get(symbol) || [];
    history.push(greeksData);

    // Keep only recent history
    if (history.length > this.maxHistorySize) {
      history.shift();
    }

    this.history.set(symbol, history);
  }

  private rebuildCacheIndex(): void {
    if (this.cache.size === 0) {
      return;
    }

    const uniqueEntries = Array.from(new Set(this.cache.values()));
    this.cache.clear();

    uniqueEntries.forEach((entry) => {
      if (!entry || typeof entry.streamerSymbol !== "string") {
        return;
      }

      const variants = deriveSymbolVariants(
        entry.streamerSymbol,
        symbolMappingService,
        entry.symbol,
      );

      const aliasKeys = buildAliasKeys(
        [
          entry.streamerSymbol,
          entry.symbol,
          variants.mappingPosition,
          variants.canonicalStreamer,
          variants.canonicalPosition,
          variants.compactSymbol,
        ],
        { hasKey: this.cache.has.bind(this.cache) },
      );

      if (aliasKeys.length === 0) {
        aliasKeys.push(entry.streamerSymbol.trim());
      }

      aliasKeys.forEach((key) => {
        if (key) {
          this.cache.set(key, entry);
        }
      });
    });
  }

  /**
   * Get cached Greeks for a symbol (supports both position and streamer symbols)
   */
  getGreeks(symbol: string): CachedGreeks | null {
    const trimmed = (symbol || "").trim();
    if (!trimmed) {
      return null;
    }

    const variants = deriveSymbolVariants(
      trimmed,
      symbolMappingService,
    );

    const aliasKeys = buildAliasKeys(
      [
        trimmed,
        variants.trimmed,
        variants.mappingPosition,
        variants.canonicalStreamer,
        variants.canonicalPosition,
        variants.compactSymbol,
      ],
      { hasKey: this.cache.has.bind(this.cache) },
    );

    if (!aliasKeys.includes(trimmed)) {
      aliasKeys.unshift(trimmed);
    }

    let cached: CachedGreeks | undefined;
    for (const key of aliasKeys) {
      cached = this.cache.get(key);
      if (cached) {
        break;
      }
    }

    if (!cached) {
      return null;
    }

    // Mark as stale but NEVER delete - data persistence is key
    const ageHours = (Date.now() - cached.timestamp) / (1000 * 60 * 60);
    const isStale = ageHours > 1; // Flag if older than 1 hour

    // Return the data with staleness indicator
    return {
      ...cached,
      isStale,
      // Optionally reduce confidence for very old data
      confidence: ageHours > 24 ? cached.confidence * 0.7 : cached.confidence,
    };
  }

  /**
   * Get all cached Greeks
   */
  getAllGreeks(): Map<string, CachedGreeks> {
    return new Map(this.cache);
  }

  /**
   * Clear cache for a symbol
   */
  clearSymbol(symbol: string): void {
    this.cache.delete(symbol);
    this.history.delete(symbol);
    this.saveToStorage();
  }

  /**
   * Clear all cache
   */
  clearAll(): void {
    this.cache.clear();
    this.history.clear();
    this.saveToStorage();
  }

  /**
   * Clean up stale data - DISABLED to prioritize data persistence
   * Keeping method for potential manual cleanup only
   */
  private cleanupStaleData(): void {
    // DISABLED: We no longer automatically clean up stale data
    // Greeks data should persist until explicitly cleared or replaced with newer data
    /*
    const now = Date.now();
    const staleThreshold = this.STALE_DATA_MINUTES * 60 * 1000 * 2; // 2x stale threshold

    let cleaned = 0;
    this.cache.forEach((cached, symbol) => {
      if (now - cached.timestamp > staleThreshold) {
        this.cache.delete(symbol);
        cleaned++;
      }
    });

    if (cleaned > 0) {
      // Removed log(`🧹 Cleaned ${cleaned} stale Greeks entries`);
      this.saveToStorage();
    }
    */
  }

  /**
   * Load from database
   */
  private async loadFromDatabase(): Promise<void> {
    try {
      // Only log in development mode
      if (process.env.NODE_ENV === "development") {
        // Removed log("📥 Loading Greeks from database...");
      }
      const response = await fetch("/api/greeks/cache", {
        credentials: 'include' as RequestCredentials,
      });

      if (!response.ok) {
        // Only log once to avoid spam
        if (!this.hasLoggedLoadError) {
          // Failed to load Greeks from database
          this.hasLoggedLoadError = true;
        }
        return;
      }

      const result: unknown = await response.json();
      const rawData = isRecord(result) ? result.data : undefined;
      const greeksArray = Array.isArray(rawData)
        ? rawData
        : isRecord(rawData)
          ? Object.values(rawData)
          : [];

      let hasNewEntries = false;

      greeksArray.forEach((entry) => {
        const parsed = this.parseCachedGreeksEntry(entry);
        if (!parsed) {
          return;
        }

        const existing = this.cache.get(parsed.streamerSymbol);
        if (!existing || parsed.timestamp > existing.timestamp) {
          this.cache.set(parsed.streamerSymbol, parsed);
          hasNewEntries = true;
        }
      });

      if (hasNewEntries) {
        this.rebuildCacheIndex();
        this.saveToStorage();
      }
    } catch {
      // Only log connection errors once to avoid spam
      if (!this.hasLoggedLoadError) {
        // Removed error("Error loading Greeks from database:", error);
        this.hasLoggedLoadError = true;
      }
    }
  }

  private hasLoggedLoadError = false;

  /**
   * Load from localStorage
   */
  private loadFromStorage(): void {
    if (typeof window === "undefined") return;

    try {
      const stored = localStorage.getItem("greeksCache");
      if (!stored) {
        return;
      }

      const parsed: unknown = JSON.parse(stored);
      const cacheEntries =
        isRecord(parsed) && Array.isArray(parsed.cache) ? parsed.cache : [];

      cacheEntries.forEach((entry) => {
        const parsedEntry = this.parseCachedGreeksEntry(entry);
        if (!parsedEntry) {
          return;
        }
        this.cache.set(parsedEntry.streamerSymbol, parsedEntry);
      });

      this.rebuildCacheIndex();

      // Loaded Greeks from storage
    } catch {
      // Removed error("Error loading Greeks cache");
    }
  }

  /**
   * Save to database
   */
  private async saveToDatabase(greeks: CachedGreeks): Promise<void> {
    try {
      const response = await fetch("/api/greeks/cache", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: 'include' as RequestCredentials,
        body: JSON.stringify({
          action: "save",
          data: {
            positionSymbol: greeks.symbol,
            streamerSymbol: greeks.streamerSymbol,
            delta: greeks.delta,
            gamma: greeks.gamma,
            theta: greeks.theta,
            vega: greeks.vega,
            rho: greeks.rho,
            timestamp: greeks.timestamp,
            lastValidUpdate: greeks.lastValidUpdate,
          },
        }),
      });

      if (!response.ok) {
        // Only log once per minute to avoid spam
        const now = Date.now();
        if (!this.lastDbErrorTime || now - this.lastDbErrorTime > 60000) {
          // Removed error("Failed to save Greeks to database:", response.status);
          this.lastDbErrorTime = now;
        }
      }
    } catch {
      // Only log connection errors once per minute to avoid spam
      const now = Date.now();
      if (!this.lastDbErrorTime || now - this.lastDbErrorTime > 60000) {
        // Removed error("Error saving Greeks to database");
        this.lastDbErrorTime = now;
      }
    }
  }

  private lastDbErrorTime: number | null = null;

  /**
   * Save to localStorage
   */
  private saveToStorage(): void {
    if (typeof window === "undefined") return;

    const cacheArray = Array.from(new Set(this.cache.values()));
    const payload = {
      cache: cacheArray,
      timestamp: Date.now(),
      version: "1.0", // Add version for future migrations
    };

    try {
      const serialized = JSON.stringify(payload);
      localStorage.setItem("greeksCache", serialized);

      // Verify save was successful
      const verified = localStorage.getItem("greeksCache");
      if (verified) {
        // Saved Greeks to localStorage
      }
    } catch (error) {
      // Removed error("Error saving Greeks cache:", error);

      // If localStorage is full, try to clear old data
      if (error instanceof Error && error.name === "QuotaExceededError") {
        // Removed warn("localStorage quota exceeded, clearing old data...");
        try {
          // Clear other old data if needed
          const keys = Object.keys(localStorage);
          keys.forEach((key) => {
            if (key.startsWith("old_") || key.includes("_backup")) {
              localStorage.removeItem(key);
            }
          });

          // Try again
          localStorage.setItem("greeksCache", JSON.stringify(payload));
          // Successfully saved after clearing old data
        } catch {
          // Failed to save even after clearing old data
        }
      }
    }
  }

  /**
   * Get statistics
   */
  getStats(): {
    totalCached: number;
    highConfidence: number;
    lowConfidence: number;
    avgUpdateCount: number;
    oldestData: Date | null;
  } {
    let highConfidence = 0;
    let lowConfidence = 0;
    let totalUpdates = 0;
    let oldestTimestamp = Date.now();

    this.cache.forEach((cached) => {
      if (cached.confidence >= this.CONFIDENCE_THRESHOLD) {
        highConfidence++;
      } else {
        lowConfidence++;
      }
      totalUpdates += cached.updateCount;
      if (cached.timestamp < oldestTimestamp) {
        oldestTimestamp = cached.timestamp;
      }
    });

    return {
      totalCached: this.cache.size,
      highConfidence,
      lowConfidence,
      avgUpdateCount: this.cache.size > 0 ? totalUpdates / this.cache.size : 0,
      oldestData: this.cache.size > 0 ? new Date(oldestTimestamp) : null,
    };
  }

  /**
   * Force save to storage (public method for manual saves)
   */
  forceSave(): void {
    this.saveToStorage();
  }

  /**
   * Reload Greeks from database (public method)
   */
  async reloadFromDatabase(): Promise<void> {
    await this.loadFromDatabase();
  }

  /**
   * Debug helper that returns AMD-related cache insights for manual inspection.
   */
  debugAMDGreeks(): {
    cacheEntries: CachedGreeks[];
    storageCount: number;
  } {
    const cacheEntries = Array.from(this.cache.values()).filter(
      (entry) => entry.symbol.includes("AMD") || entry.streamerSymbol.includes("AMD"),
    );

    let storageCount = 0;
    if (typeof window === "undefined") {
      return {
        cacheEntries,
        storageCount,
      };
    }

    try {
      const stored = localStorage.getItem("greeksCache");
      if (stored) {
        const parsed: unknown = JSON.parse(stored);
        if (isRecord(parsed) && Array.isArray(parsed.cache)) {
          storageCount = parsed.cache.reduce((count, item) => {
            if (!isRecord(item)) {
              return count;
            }
            const symbol = readString(item, ["symbol", "streamerSymbol"]);
            return symbol?.includes("AMD") ? count + 1 : count;
          }, 0);
        }
      }
    } catch {
      // Swallow parsing errors; debug helper should never throw.
    }

    return {
      cacheEntries,
      storageCount,
    };
  }

  /**
   * Check storage status
   */
  getStorageInfo(): {
    hasStoredData: boolean;
    storedCount: number;
    storageSize: number;
    lastSaved: Date | null;
  } {
    if (typeof window === "undefined") {
      return {
        hasStoredData: false,
        storedCount: 0,
        storageSize: 0,
        lastSaved: null,
      };
    }

    try {
      const stored = localStorage.getItem("greeksCache");
      if (!stored) {
        return {
          hasStoredData: false,
          storedCount: 0,
          storageSize: 0,
          lastSaved: null,
        };
      }

      const parsed: unknown = JSON.parse(stored);
      if (!isRecord(parsed)) {
        return {
          hasStoredData: false,
          storedCount: 0,
          storageSize: 0,
          lastSaved: null,
        };
      }

      const storedCount = Array.isArray(parsed.cache) ? parsed.cache.length : 0;
      const timestamp = readNumber(parsed, ["timestamp"]);
      return {
        hasStoredData: true,
        storedCount,
        storageSize: stored.length,
        lastSaved: typeof timestamp === "number" ? new Date(timestamp) : null,
      };
    } catch {
      return {
        hasStoredData: false,
        storedCount: 0,
        storageSize: 0,
        lastSaved: null,
      };
    }
  }
}

// Singleton instance
export const greeksCacheService = new GreeksCacheService();

// Expose to window for debugging in development
if (typeof window !== "undefined" && process.env.NODE_ENV === "development") {
  const windowWithService = window as Window & {
    greeksCacheService?: GreeksCacheService;
  };
  windowWithService.greeksCacheService = greeksCacheService;
  // Removed startup console log to reduce noise
}
